/**
 * Team display names — ONE rule for every sport (HYDRA_API.md "Team names —
 * `canonicalTeamNames`, all sports"):
 *
 *     displayName(gameKey, code) = namesByGame[gameKey]?.[code] ?? code
 *
 * Hydra resolves the name per sport (franchise tables, Poly club names, the
 * Kalshi leg's own subtitle, the 1v1 minter's player names) and stamps the
 * game's `{code: name}` map on EVERY row that carries `canonicalGameKeyV2` —
 * Kalshi, Polymarket and Poly US, every class, props and ALT rows included.
 * g1 holds no per-sport naming data: no code table, no subtitle read, no ticker
 * parse, no sport or league list decides what a team is called.
 *
 * `namesByGame[gameKey]` is the map from the LATEST keyed row of that game, from
 * any venue and any engine, replaced wholesale — INCLUDING when that row carries
 * no map. A producer that stops naming a side surfaces as the code, never masked
 * by a held value (the #685 index discipline). That is safe because of hydra's
 * per-game invariant: within one identity generation every keyed row of a game
 * carries the identical map, so a prop or ALT row cannot clear names a game row
 * set, and arrival order does not matter.
 */
import { foldGameKeySport, parseGameKeyV2, promoteToGameKeyV2 } from '../services/analyticsGameKey';

const _namesByGame = new Map<string, ReadonlyMap<string, string>>();

/** The index's key for any spelling of a game key: the promoted 5-seg form with
 *  the sport segment folded (`ncaaf|…` and Poly US's `cfb|…` are one game).
 *  Null for anything that is not a canonical game key (event tickers, synthetic
 *  panel keys). */
function namesKey(gameKey: string | null | undefined): string | null {
  const parts = parseGameKeyV2(gameKey);
  if (!parts?.sport) return null;
  return foldGameKeySport(promoteToGameKeyV2(gameKey));
}

/** The wire map, validated against the key it rides on: keys are upper-cased
 *  and must be one of the key's two codes (the contract asserts it; a stray key
 *  must not name a code it has no authority over), values non-empty trimmed
 *  strings. Null when nothing valid remains — the "absent" state. */
function namesFromWire(gameKey: string, wire: unknown): ReadonlyMap<string, string> | null {
  if (!wire || typeof wire !== 'object' || Array.isArray(wire)) return null;
  const parts = parseGameKeyV2(gameKey);
  if (!parts) return null;
  const names = new Map<string, string>();
  for (const [code, name] of Object.entries(wire as Record<string, unknown>)) {
    const c = code.trim().toUpperCase();
    if (c !== parts.teamLo && c !== parts.teamHi) continue;
    if (typeof name !== 'string' || !name.trim()) continue;
    names.set(c, name.trim());
  }
  return names.size > 0 ? names : null;
}

/** Record what one keyed row says its game's sides are called. Called for every
 *  admitted frame that carries `canonicalGameKeyV2`; the row's map REPLACES the
 *  game's entry, and a row without one clears it. A row with no v2 key has no
 *  authority over any game and must not be passed here. */
export function recordGameTeamNames(gameKey: string | null | undefined, wire: unknown): void {
  const key = namesKey(gameKey);
  if (!key) return;
  const names = namesFromWire(key, wire);
  if (names) _namesByGame.set(key, names);
  else _namesByGame.delete(key);
}

/** Hydra's name for one side of a game, or null when it has not named it. For
 *  callers that need to know whether a label is named (a highlight token). */
export function gameTeamName(gameKey: string | null | undefined, code: string | null | undefined): string | null {
  if (!code) return null;
  const key = namesKey(gameKey);
  if (!key) return null;
  return _namesByGame.get(key)?.get(code.trim().toUpperCase()) ?? null;
}

/** THE display rule: hydra's name for the side, else the code itself. */
export function teamDisplayName(gameKey: string | null | undefined, code: string): string {
  return gameTeamName(gameKey, code) ?? code;
}
